import { useRef, useState } from "react";
import { AlertCircle, FileUp, Upload } from "lucide-react";
import { validateAndFormatCsv } from "@/lib/returns";

export default function ImportDialog({ existingOrderIds, onImport }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const closeDialog = () => {
    setOpen(false);
    setError("");
    setFileName("");
    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  const onFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const result = validateAndFormatCsv(String(reader.result), existingOrderIds);
      if (result.error) {
        setError(result.error);
        return;
      }
      onImport(result.items);
      closeDialog();
    };
    reader.onerror = () => setError("We could not read this file. Please choose a valid CSV.");
    reader.readAsText(file);
  };

  return (
    <>
      <button
        className="button button-primary"
        type="button"
        onClick={() => setOpen(true)}
        data-testid="import-csv-button"
      >
        <Upload size={17} aria-hidden="true" />
        Import CSV
      </button>
      {open && (
        <div className="modal-backdrop" role="presentation">
          <section
            className="import-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-csv-title"
            data-testid="import-csv-dialog"
          >
            <button
              className="modal-close"
              type="button"
              onClick={closeDialog}
              aria-label="Close import dialog"
              data-testid="close-import-dialog-button"
            >
              ×
            </button>
            <div className="modal-icon"><FileUp size={25} aria-hidden="true" /></div>
            <p className="eyebrow">Returns spreadsheet</p>
            <h2 id="import-csv-title">Import a CSV file</h2>
            <p className="modal-copy">
              We validate every row before adding it to your local returns list.
            </p>
            <label className="file-drop" htmlFor="returns-csv-upload">
              <Upload size={23} aria-hidden="true" />
              <strong>{fileName || "Choose CSV file"}</strong>
              <span>CSV only · required columns checked</span>
            </label>
            <input
              id="returns-csv-upload"
              ref={fileRef}
              className="sr-only"
              type="file"
              accept=".csv,text/csv"
              onChange={onFileChange}
              data-testid="returns-csv-file-input"
            />
            {error && (
              <p className="import-error" role="alert" data-testid="csv-import-error">
                <AlertCircle size={17} aria-hidden="true" />
                {error}
              </p>
            )}
            <p className="required-columns">
              Required: order_id, barcode, product_name, category, variant, mrp,
              return_reason, image_url
            </p>
          </section>
        </div>
      )}
    </>
  );
}